/**
 * Permissão por área do sistema (Auditoria 2026-09, BUG-015).
 *
 * Antes nenhuma rota exigia permissão: qualquer funcionário logado (ex.: estagiário) abria
 * financeiro, usuários e permissões digitando o endereço, e o menu mostrava tudo a todos.
 *
 * Regras:
 *  - Dono da clínica (não subordinado) e admin da plataforma ('all') acessam tudo.
 *  - Funcionários precisam de qualquer chave do grupo da área. As chaves existem em três
 *    "dialetos" (padrão por papel, runtime e catálogo da tela Permissões); todos são aceitos.
 *  - Áreas não listadas continuam liberadas a qualquer usuário da clínica.
 *
 * Usado pela proteção de rota (ProtectedRoute) e pelo filtro do menu — uma única fonte.
 */

export type AccessArea =
  | 'financial'
  | 'financial_reports'
  | 'billing'
  | 'financial_registry'
  | 'users'
  | 'settings'
  | 'ehr'
  | 'owner_only';

interface AreaRule {
  keys: string[];
  prefixes?: string[];
  label: string;
}

const FINANCIAL_KEYS = [
  'financial.view',
  'financial.edit',
  'financial_view',
  'financial_edit',
  'view_financial',
  'manage_financial',
];

const AREA_RULES: Record<AccessArea, AreaRule> = {
  financial: { keys: FINANCIAL_KEYS, prefixes: ['fin.'], label: 'Financeiro' },
  financial_reports: {
    keys: [...FINANCIAL_KEYS, 'rel.financeiro'],
    prefixes: ['fin.', 'rel.comiss', 'rel.faturamento', 'rel.pagar', 'rel.receber', 'rel.centros', 'rel.recibos', 'rel.vendas'],
    label: 'Relatórios financeiros',
  },
  billing: {
    keys: [...FINANCIAL_KEYS, 'billing.viewSubscriptions', 'billing.managePlans', 'billing.runAutomation'],
    prefixes: ['fin.cobrancas'],
    label: 'Cobrança automática',
  },
  financial_registry: {
    keys: FINANCIAL_KEYS,
    prefixes: ['fin.', 'cad.precos', 'cad.centros_custo', 'cad.formas_pagamento'],
    label: 'Cadastros financeiros',
  },
  users: {
    keys: ['admin_access', 'manage_users'],
    prefixes: ['ger.usuarios', 'ger.permissoes'],
    label: 'Usuários e permissões',
  },
  settings: {
    keys: ['admin_access'],
    prefixes: ['ger.config', 'ger.whatsapp', 'ger.notificacoes'],
    label: 'Configurações da clínica',
  },
  ehr: {
    keys: ['manage_ehr', 'view_ehr', 'ehr_view', 'ehr_edit'],
    prefixes: ['cliente.prontuario', 'agendas.evolucao', 'clin.'],
    label: 'Prontuário',
  },
  owner_only: { keys: [], label: 'Administração da clínica' },
};

/** Área protegida de um caminho (ignora query string). `null` = liberado à equipe. */
export function areaForPath(path: string): AccessArea | null {
  const p = (path.split('?')[0] || '/').replace(/\/+$/, '') || '/';
  const starts = (prefix: string) => p === prefix || p.startsWith(`${prefix}/`);

  if (starts('/financial/governance')) return 'owner_only';
  if (starts('/financial/billing')) return 'billing';
  if (starts('/financial')) return 'financial';

  if (
    starts('/reports/financial') ||
    starts('/reports/professional-commissioning') ||
    starts('/relatorio/atendimento-profissional') ||
    starts('/reports/product-sales') ||
    starts('/relatorio/vendas') ||
    starts('/advanced-reports')
  ) {
    return 'financial_reports';
  }

  if (starts('/price-table') || starts('/cost-centers') || starts('/payment-methods')) {
    return 'financial_registry';
  }

  if (
    starts('/system-users') ||
    starts('/user') ||
    starts('/permissions') ||
    starts('/permissao') ||
    starts('/user-management')
  ) {
    return 'users';
  }

  if (
    starts('/system-definitions') ||
    starts('/definicoes') ||
    starts('/client-center') ||
    starts('/online-booking') ||
    starts('/configuracao-agendamento-remoto') ||
    starts('/omnichannel/channels') ||
    starts('/omnichannel/messages')
  ) {
    return 'settings';
  }

  if (
    starts('/ehr') ||
    starts('/patient-ai') ||
    /^\/patient\/[^/]+\/(ehr|dynamic-ehr|ai-analysis)$/.test(p)
  ) {
    return 'ehr';
  }

  return null;
}

export interface AccessContext {
  /** Dono da clínica: usuário que não é subordinado. */
  isClinicOwner: boolean;
  permissions: string[];
}

/**
 * Áreas financeiras só para o dono da clínica (decisão do titular, 03/10/2026 — revisão R10):
 * funcionários não veem faturamento, contas nem comissões dos colegas, mesmo que o papel
 * "professional" traga financial.view/edit por padrão.
 */
const OWNER_ONLY_AREAS: ReadonlySet<AccessArea> = new Set<AccessArea>([
  'financial',
  'financial_reports',
  'billing',
  'financial_registry',
  'owner_only',
]);

export function canAccessArea(area: AccessArea | null, ctx: AccessContext): boolean {
  if (!area) return true;
  if (ctx.isClinicOwner || ctx.permissions.includes('all')) return true;
  if (OWNER_ONLY_AREAS.has(area)) return false;
  const rule = AREA_RULES[area];
  if (rule.keys.some((k) => ctx.permissions.includes(k))) return true;
  const prefixes = rule.prefixes || [];
  return ctx.permissions.some((perm) => prefixes.some((pre) => perm.startsWith(pre)));
}

export function canAccessPath(path: string, ctx: AccessContext): boolean {
  return canAccessArea(areaForPath(path), ctx);
}

export function areaLabel(area: AccessArea): string {
  return AREA_RULES[area].label;
}

/** Dono da clínica = não é subordinado (subordinados têm isSubordinate/adminId gravados pelo servidor). */
export function isClinicOwnerProfile(user: { isSubordinate?: boolean; adminId?: string | null } | null | undefined): boolean {
  if (!user) return false;
  return !user.isSubordinate && !user.adminId;
}

/** Remove do menu os itens sem acesso; grupos que ficam vazios somem. */
export function filterNavTree<T extends { path?: string; children?: T[] }>(
  items: T[],
  canAccess: (path: string) => boolean
): T[] {
  const out: T[] = [];
  for (const item of items) {
    if (item.children?.length) {
      const children = filterNavTree(item.children, canAccess);
      if (children.length > 0) out.push({ ...item, children });
    } else if (!item.path || item.path.startsWith('#') || canAccess(item.path)) {
      out.push(item);
    }
  }
  return out;
}
